import { lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import ProjectLayout from '../../components/ProjectLayout';
import {
  FullScreenLoader,
  Loading,
  RequireAuth,
  RequireProjectRole,
  RequireSuperadmin,
} from '../../components/routing';
import { useAuth, useProjectRole } from '../../lib/auth';
import LoginPage from '../../pages/LoginPage';
import ProjectsPage from '../../pages/ProjectsPage';

/**
 * The building surface — admin.windysignal.com.
 *
 * Everything that shapes a project: the API builder, datasources and their
 * table designer, run logs, members and settings. Dashboards are here too,
 * because this is where they are edited.
 */
const BuilderPage = lazy(() => import('../../pages/BuilderPage'));
const DashboardListPage = lazy(() => import('../../pages/DashboardListPage'));
const DashboardViewPage = lazy(() => import('../../pages/DashboardViewPage'));
const DatasourcesPage = lazy(() => import('../../pages/DatasourcesPage'));
const EndpointDocsPage = lazy(() => import('../../pages/EndpointDocsPage'));
const EndpointsPage = lazy(() => import('../../pages/EndpointsPage'));
const LogsPage = lazy(() => import('../../pages/LogsPage'));
const McpPage = lazy(() => import('../../pages/McpPage'));
const OverviewPage = lazy(() => import('../../pages/OverviewPage'));
const SchemaDesignerPage = lazy(() => import('../../pages/SchemaDesignerPage'));
const SettingsPage = lazy(() => import('../../pages/SettingsPage'));

/**
 * One URL, two faces: editors get the flow canvas, everyone else gets the
 * read-only API reference. A viewer never loads the builder bundle at all.
 */
function EndpointRoute() {
  const { projectId } = useParams();
  const { canEdit } = useProjectRole(projectId);
  return canEdit ? (
    <Loading label="Loading the builder">
      <BuilderPage />
    </Loading>
  ) : (
    <Loading>
      <EndpointDocsPage />
    </Loading>
  );
}

export default function App() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <FullScreenLoader /> : user ? <Navigate to="/" replace /> : <LoginPage />}
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<ProjectsPage />} />

        {/* Instance-wide, so it sits above the project tree rather than inside
            it: one admin token reaches every project at once.

            Namespaced under /instance because the server owns /mcp itself —
            that is the endpoint AI clients POST to, registered ahead of the SPA
            fallback, so a page there would never be reached. */}
        <Route
          path="instance/mcp"
          element={
            <RequireSuperadmin>
              <Loading label="Loading MCP settings">
                <McpPage />
              </Loading>
            </RequireSuperadmin>
          }
        />

        <Route path="projects/:projectId" element={<ProjectLayout />}>
          <Route
            index
            element={
              <Loading>
                <OverviewPage />
              </Loading>
            }
          />

          <Route
            path="endpoints"
            element={
              <Loading>
                <EndpointsPage />
              </Loading>
            }
          />
          <Route path="endpoints/:endpointId" element={<EndpointRoute />} />

          <Route
            path="dashboards"
            element={
              <Loading>
                <DashboardListPage />
              </Loading>
            }
          />
          <Route
            path="dashboards/:dashboardId"
            element={
              <Loading label="Loading dashboard">
                <DashboardViewPage />
              </Loading>
            }
          />

          {/* Everything below exposes how the project is built, or who builds it. */}
          <Route
            path="datasources"
            element={
              <RequireProjectRole minRole="editor">
                <Loading>
                  <DatasourcesPage />
                </Loading>
              </RequireProjectRole>
            }
          />
          <Route
            path="datasources/:datasourceId/schema"
            element={
              <RequireProjectRole minRole="editor">
                <Loading>
                  <SchemaDesignerPage />
                </Loading>
              </RequireProjectRole>
            }
          />
          <Route
            path="logs"
            element={
              <RequireProjectRole minRole="editor">
                <Loading>
                  <LogsPage />
                </Loading>
              </RequireProjectRole>
            }
          />
          <Route
            path="settings/*"
            element={
              <RequireProjectRole minRole="editor">
                <Loading>
                  <SettingsPage />
                </Loading>
              </RequireProjectRole>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
